import { AxiosResponse } from "axios";

import { API } from "../index";
import {
  GroupReservationAdminMarketer,
  GroupReservationAgencyLink,
  GroupReservationAgencyLinkPayload,
  GroupReservationConfirmBankTransferPayload,
  GroupReservationMarketerSummary,
  GroupReservationOptionProduct,
  GroupReservationOptionProductPayload,
  GroupReservationRejectPayload,
  GroupReservationRequest,
  GroupReservationRequestCreatePayload,
  GroupReservationRequestFilters,
  GroupReservationRequestUpdatePayload,
  GroupReservationReservationIdsPayload,
  GroupReservationRevertPayload,
  MarketerSettlementProfile,
  MarketerSettlementProfilePayload,
  PaginatedResponse,
} from "../types/group-reservation";

const ADMIN_BASE = "/api/v2/group-reservation/admin";

const cleanParams = <T extends Record<string, unknown>>(params?: T): Partial<T> => {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== "" && value !== undefined && value !== null)
  ) as Partial<T>;
};

export const groupReservationService = {
  getRequests: (
    params?: GroupReservationRequestFilters
  ): Promise<AxiosResponse<PaginatedResponse<GroupReservationRequest>>> => {
    return API.get(`${ADMIN_BASE}/requests/`, { params: cleanParams(params) });
  },

  getRequest: (id: number): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.get(`${ADMIN_BASE}/requests/${id}/`);
  },

  createRequest: (payload: GroupReservationRequestCreatePayload): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/`, payload);
  },

  updateRequest: (
    id: number,
    payload: GroupReservationRequestUpdatePayload
  ): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.patch(`${ADMIN_BASE}/requests/${id}/`, payload);
  },

  linkReservations: (
    id: number,
    payload: GroupReservationReservationIdsPayload
  ): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/link-reservations/`, payload);
  },

  unlinkReservations: (
    id: number,
    payload: GroupReservationReservationIdsPayload
  ): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/unlink-reservations/`, payload);
  },

  startBankTransfer: (id: number): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/start-bank-transfer/`);
  },

  confirmBankTransfer: (
    id: number,
    payload: GroupReservationConfirmBankTransferPayload
  ): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/confirm-bank-transfer/`, payload);
  },

  approve: (id: number): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/approve/`);
  },

  cancelApproval: (id: number): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/cancel-approval/`);
  },

  reject: (id: number, payload: GroupReservationRejectPayload): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/reject/`, payload);
  },

  revert: (id: number, payload: GroupReservationRevertPayload): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post(`${ADMIN_BASE}/requests/${id}/revert/`, payload);
  },

  getOptionProducts: (): Promise<AxiosResponse<PaginatedResponse<GroupReservationOptionProduct>>> => {
    return API.get(`${ADMIN_BASE}/option-products/`);
  },

  createOptionProduct: (
    payload: GroupReservationOptionProductPayload
  ): Promise<AxiosResponse<GroupReservationOptionProduct>> => {
    return API.post(`${ADMIN_BASE}/option-products/`, payload);
  },

  updateOptionProduct: (
    id: number,
    payload: Partial<GroupReservationOptionProductPayload>
  ): Promise<AxiosResponse<GroupReservationOptionProduct>> => {
    return API.patch(`${ADMIN_BASE}/option-products/${id}/`, payload);
  },

  deleteOptionProduct: (id: number): Promise<AxiosResponse<void>> => {
    return API.delete(`${ADMIN_BASE}/option-products/${id}/`);
  },

  getAdminMarketers: (): Promise<AxiosResponse<PaginatedResponse<GroupReservationAdminMarketer>>> => {
    return API.get(`${ADMIN_BASE}/marketers/`);
  },

  getAgencyLinks: (): Promise<AxiosResponse<PaginatedResponse<GroupReservationAgencyLink>>> => {
    return API.get(`${ADMIN_BASE}/agency-links/`);
  },

  createAgencyLink: (
    payload: GroupReservationAgencyLinkPayload
  ): Promise<AxiosResponse<GroupReservationAgencyLink>> => {
    return API.post(`${ADMIN_BASE}/agency-links/`, payload);
  },

  updateAgencyLink: (
    id: number,
    payload: Partial<GroupReservationAgencyLinkPayload>
  ): Promise<AxiosResponse<GroupReservationAgencyLink>> => {
    return API.patch(`${ADMIN_BASE}/agency-links/${id}/`, payload);
  },

  getMarketerProfile: (): Promise<AxiosResponse<MarketerSettlementProfile>> => {
    return API.get("/api/v2/group-reservation/marketer/me/profile/");
  },

  updateMarketerProfile: (
    payload: MarketerSettlementProfilePayload
  ): Promise<AxiosResponse<MarketerSettlementProfile>> => {
    return API.patch("/api/v2/group-reservation/marketer/me/profile/", payload);
  },

  getMarketerSummary: (): Promise<AxiosResponse<GroupReservationMarketerSummary>> => {
    return API.get("/api/v2/group-reservation/marketer/me/summary/");
  },

  getMarketerRequests: (
    params?: GroupReservationRequestFilters
  ): Promise<AxiosResponse<PaginatedResponse<GroupReservationRequest>>> => {
    return API.get("/api/v2/group-reservation/marketer/me/requests/", { params: cleanParams(params) });
  },

  createMarketerRequest: (
    payload: GroupReservationRequestCreatePayload
  ): Promise<AxiosResponse<GroupReservationRequest>> => {
    return API.post("/api/v2/group-reservation/marketer/me/requests/", payload);
  },
};
